import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import Button from 'reactstrap/lib/Button';
import { InventoryTrimSummaryEntities } from 'client/data/models/inventory-core-configurations';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities } from 'client/data/models/visitor';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { FIND_BEST_PRICE_FORM_NAME } from 'site-modules/personalized-search/constants/tracking';

export function NewPricingButton({
  vehicle,
  ctaText,
  trimSummary = null,
  visitorLocation = null,
  pageName = null,
  btnClassName = undefined,
  isMobile = false,
  creativeId = undefined,
  getLeadFormSrpLinkAttributes = undefined,
  onDrawerHide = undefined,
}) {
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle();

  if (!trimSummary) {
    return null;
  }

  const {
    model: { name: modelName },
  } = vehicle;
  const {
    pricing: { invoicePrice, totalMsrp, estimatedSavings },
    trimName,
    styleId,
    hasLeadFormDealers,
  } = trimSummary;
  const ESP = get(trimSummary, 'pricing.suggestedPrice', totalMsrp);

  const dataTrackingLeadForm = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: FIND_BEST_PRICE_FORM_NAME,
  });

  const explainingText = `${
    invoicePrice
      ? `Knowing the Edmunds estimated invoice price for the ${modelName} ${trimName} is **${getPriceString(
          invoicePrice
        )}** is helpful, but not the whole story.`
      : 'Did you know? You should know exactly what you should pay for a car before you test drive it.'
  }\n\nWe'll show you what to pay on the next page.`;

  return (
    <div className="new-pricing-button">
      {hasLeadFormDealers && (
        <Button
          color="blue-50"
          size="lg"
          onClick={toggleLeadFormOpen}
          className={classnames('pricing-btn text-white text-transform-none fw-medium', btnClassName)}
          data-tracking-parent={creativeId}
        >
          {ctaText}
        </Button>
      )}
      <TransparentPricingLeadFormDrawer
        isOpen={isLeadFormOpen}
        onDrawerToggle={toggleLeadFormOpen}
        params={{
          styleId,
          trim: trimName,
          vehicle,
          zipCode: visitorLocation?.zipCode,
          city: visitorLocation?.city,
        }}
        options={{
          isMobile,
          bestDealPrice: ESP,
          creativeId: dataTrackingLeadForm,
          estimatedSavings,
          explainingTextDesktop: explainingText,
          explainingTextMobile: explainingText,
        }}
        creativeId={dataTrackingLeadForm}
        getLeadFormSrpLinkAttributes={getLeadFormSrpLinkAttributes}
        onDrawerHide={onDrawerHide}
      />
    </div>
  );
}

NewPricingButton.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  ctaText: PropTypes.string.isRequired,
  trimSummary: InventoryTrimSummaryEntities.TrimSummary,
  visitorLocation: VisitorEntities.Location,
  pageName: PropTypes.string,
  btnClassName: PropTypes.string,
  isMobile: PropTypes.bool,
  creativeId: PropTypes.string,
  getLeadFormSrpLinkAttributes: PropTypes.func,
  onDrawerHide: PropTypes.func,
};
